import { ChevronLeft, ChevronRight } from "lucide-react";

import { Vendor } from "../../types/vendorManagement";
import { cn } from "../../utils/cn";

type VendorManagementListProps = {
  vendors: Vendor[];
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onSelectVendor: (vendorId: string) => void;
};

const formatCurrency = (amount: number) =>
  `OMR ${new Intl.NumberFormat("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(amount)}`;

function VendorManagementList({
  vendors,
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  onSelectVendor,
}: VendorManagementListProps) {
  const safeTotalPages = Math.max(1, totalPages);

  return (
    <div className="rounded-[24px] border border-[#E8EEF9] bg-white shadow-[0_16px_42px_rgba(26,94,249,0.08)]">
      <div className="flex flex-col gap-4 border-b border-[#E8EEF9] px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
        <p className="text-sm font-medium text-[#617064]">
          <span className="font-semibold text-[#223126]">{totalCount}</span>{" "}
          Vendors Found
        </p>
        <div className="flex items-center gap-3 text-sm text-[#8A948D]">
          <span>Show</span>
          <div className="rounded-[10px] border border-[#D8E3FF] bg-[#F4F7FF] px-3 py-2 font-semibold text-[#1A5EF9]">
            10
          </div>
          <span>Entries</span>
          <button
            type="button"
            className="rounded-[10px] border border-[#D8E3FF] p-2 text-[#8AA1D1] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() => onPageChange(Math.max(0, currentPage - 1))}
            disabled={currentPage === 0}
          >
            <ChevronLeft className="size-4" />
          </button>
          {Array.from({ length: safeTotalPages }, (_, page) => page).map(
            (page) => (
              <button
                key={page}
                type="button"
                className={cn(
                  "min-w-10 rounded-[10px] border px-3 py-2 text-sm font-medium transition-colors",
                  currentPage === page
                    ? "border-[#9ACA3C] bg-[#9ACA3C] text-white"
                    : "border-[#D8E3FF] bg-white text-[#8AA1D1] hover:border-[#AFC5FF] hover:text-[#456CC4]",
                  currentPage === page
                    ? "border-[#1A5EF9] bg-[#1A5EF9] text-white"
                    : "",
                )}
                onClick={() => onPageChange(page)}
              >
                {page + 1}
              </button>
            ),
          )}
          <button
            type="button"
            className="rounded-[10px] border border-[#D8E3FF] p-2 text-[#1A5EF9] disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() =>
              onPageChange(Math.min(safeTotalPages - 1, currentPage + 1))
            }
            disabled={currentPage >= safeTotalPages - 1}
          >
            <ChevronRight className="size-4" />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-[#F4F7FC] text-left text-sm font-semibold text-[#344338]">
              <th className="px-6 py-4">Vendor Name</th>
              <th className="px-6 py-4">Contact Person</th>
              <th className="px-6 py-4">Phone Number</th>
              <th className="px-6 py-4">Credit Period</th>
              <th className="px-6 py-4">Credit Limit</th>
              <th className="px-6 py-4">Outstanding</th>
              <th className="px-6 py-4">Overdue Amount</th>
              <th className="px-6 py-4">Available Credit</th>
            </tr>
          </thead>
          <tbody>
            {vendors.length > 0 ? (
              vendors.map((vendor) => (
                <tr
                  key={vendor.id}
                  className="cursor-pointer border-t border-[#EEF2F9] text-sm text-[#405144] transition-colors hover:bg-[#F7FAFF]"
                  onClick={() => onSelectVendor(vendor.id)}
                >
                  <td className="px-6 py-5 font-semibold text-[#2D3B30]">
                    {vendor.vendorName}
                  </td>
                  <td className="px-6 py-5">{vendor.contactPerson}</td>
                  <td className="px-6 py-5">{vendor.phoneNumber}</td>
                  <td className="px-6 py-5">{vendor.creditPeriod} days</td>
                  <td className="px-6 py-5">
                    {formatCurrency(vendor.creditLimit)}
                  </td>
                  <td className="px-6 py-5">
                    {formatCurrency(vendor.outstanding)}
                  </td>
                  <td className="px-6 py-5">
                    {formatCurrency(vendor.overdueAmount)}
                  </td>
                  <td
                    className={cn(
                      "px-6 py-5 font-semibold",
                      vendor.availableCredit < 0
                        ? "text-[#DB5D5D]"
                        : "text-[#405144]",
                    )}
                  >
                    {formatCurrency(vendor.availableCredit)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={8}
                  className="px-6 py-12 text-center text-sm text-[#95A097]"
                >
                  No vendors match the current search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default VendorManagementList;
